import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import FormHelperText from '@mui/material/FormHelperText'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import OutlinedInput from '@mui/material/OutlinedInput'
import Typography from '@mui/material/Typography'
import { PasswordChecklist } from '@/features/auth/components/PasswordChecklist'
import {
  passwordChangeSchema,
  type PasswordChangeFormData,
} from '@/features/account/lib/account-settings-schema'
import { usePasswordChangeFlow } from '@/features/account/lib/use-password-change-flow'
import {
  accountPasswordConfirmChecklistGroupSx,
  accountPasswordFieldBlockSx,
  accountPasswordFieldLabelSx,
  accountPasswordFieldsStackSx,
  accountPasswordHeaderSx,
  accountPasswordInputSx,
  accountPasswordPrimaryButtonSx,
  accountPasswordSubtitleSx,
  accountPasswordTabSx,
  accountPasswordTitleSx,
  accountPasswordVerifiedInputSx,
  accountPasswordVerifiedMessageSx,
} from '@/features/account/lib/account-settings-tokens'
import { PasswordVisibilityIcon } from '@/shared/lib/password-visibility'

function PasswordField({
  id,
  label,
  value,
  onChange,
  showPassword,
  onToggleVisibility,
  placeholder,
  disabled,
  error,
  verifiedLocked,
  testId,
}: Readonly<{
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  showPassword: boolean
  onToggleVisibility: () => void
  placeholder: string
  disabled?: boolean
  error?: boolean
  verifiedLocked?: boolean
  testId: string
}>) {
  return (
    <Box sx={accountPasswordFieldBlockSx}>
      <Typography component="label" htmlFor={id} sx={accountPasswordFieldLabelSx}>
        {label}
      </Typography>
      <OutlinedInput
        id={id}
        fullWidth
        type={showPassword ? 'text' : 'password'}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        sx={{
          ...accountPasswordInputSx,
          ...(verifiedLocked ? accountPasswordVerifiedInputSx : {}),
          ...(error ? { '& fieldset': { borderColor: '#ff5959' } } : {}),
        }}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={onToggleVisibility}
              edge="end"
              disabled={disabled}
              sx={{ p: 0, color: '#64748b' }}
            >
              <PasswordVisibilityIcon visible={showPassword} size={18} />
            </IconButton>
          </InputAdornment>
        }
      />
    </Box>
  )
}

function formatLockout(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export function ChangePasswordTab() {
  const form = useForm<PasswordChangeFormData>({
    resolver: zodResolver(passwordChangeSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
    mode: 'onChange',
  })

  const flow = usePasswordChangeFlow(form)

  return (
    <Box sx={accountPasswordTabSx} data-testid="account-change-password-tab">
      <Box sx={accountPasswordHeaderSx}>
        <Typography sx={accountPasswordTitleSx}>Change Password</Typography>
        <Typography sx={accountPasswordSubtitleSx}>Update your account password</Typography>
      </Box>

      <Box sx={accountPasswordFieldsStackSx}>
        <Box>
          <PasswordField
            id="current-password"
            label="Current Password"
            value={flow.currentPassword}
            onChange={flow.setCurrentPassword}
            showPassword={flow.showCurrent}
            onToggleVisibility={() => flow.setShowCurrent((v) => !v)}
            placeholder="Enter your current Password"
            disabled={flow.isLockedOut || flow.currentVerified}
            verifiedLocked={flow.currentVerified}
            error={Boolean(flow.currentError)}
            testId="account-current-password"
          />
          {flow.currentVerified ? (
            <Typography
              sx={accountPasswordVerifiedMessageSx}
              data-testid="account-current-password-verified"
            >
              Current password verified. Please enter and confirm your new password.
            </Typography>
          ) : null}
          {flow.currentError ? (
            <FormHelperText error sx={{ mt: '8px', maxWidth: 403, mx: 0 }}>
              {flow.currentError}
              {flow.isLockedOut ? ` Try again in ${formatLockout(flow.lockoutRemaining)}.` : ''}
            </FormHelperText>
          ) : null}
        </Box>

        {flow.currentVerified ? (
          <>
            <PasswordField
              id="new-password"
              label="New Password"
              value={flow.newPassword ?? ''}
              onChange={(v) => flow.setValue('newPassword', v, { shouldValidate: true })}
              showPassword={flow.showNew}
              onToggleVisibility={() => flow.setShowNew((v) => !v)}
              placeholder="Enter New Password"
              disabled={flow.isLockedOut}
              testId="account-new-password"
            />
            <Box sx={accountPasswordConfirmChecklistGroupSx}>
              <Box>
                <PasswordField
                  id="confirm-password"
                  label="Confirm Password"
                  value={flow.confirmPassword ?? ''}
                  onChange={(v) => flow.setValue('confirmPassword', v, { shouldValidate: true })}
                  showPassword={flow.showConfirm}
                  onToggleVisibility={() => flow.setShowConfirm((v) => !v)}
                  placeholder="Confirm New Password"
                  disabled={flow.isLockedOut || !flow.rulesComplete}
                  error={Boolean(flow.confirmInlineError)}
                  testId="account-confirm-password"
                />
                {flow.confirmInlineError ? (
                  <FormHelperText error sx={{ mt: '8px', maxWidth: 403, mx: 0 }}>
                    {flow.confirmInlineError}
                  </FormHelperText>
                ) : null}
              </Box>
              <PasswordChecklist password={flow.newPassword ?? ''} />
            </Box>
          </>
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '48px', mt: '27px' }}>
        <Button
          sx={accountPasswordPrimaryButtonSx}
          onClick={flow.handlePrimaryClick}
          disabled={flow.primaryDisabled || flow.primaryPending}
          data-testid="account-update-password"
        >
          <Box
            component="span"
            sx={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              minHeight: 16,
            }}
          >
            {flow.primaryPending ? (
              <CircularProgress
                size={20}
                color="inherit"
                sx={{ position: 'absolute' }}
                aria-label="Loading"
              />
            ) : null}
            <Box
              component="span"
              sx={{ visibility: flow.primaryPending ? 'hidden' : 'visible' }}
            >
              Change Password
            </Box>
          </Box>
        </Button>
      </Box>
    </Box>
  )
}
